import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import axios from 'axios';

export default function APSCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing APS authorization...');
  const [error, setError] = useState('');
  const [callbackProcessed, setCallbackProcessed] = useState(false);

  useEffect(() => {
    // Wait for auth loading to complete before checking authentication
    if (loading) {
      return;
    }

    if (!isAuthenticated) {
      // User must be logged in to complete APS OAuth
      // Store callback params so we can resume after login
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      if (code) {
        sessionStorage.setItem('aps_callback_code', code);
        sessionStorage.setItem('aps_callback_state', state || '');
      }
      navigate('/login');
      return;
    }

    // Only process callback once
    if (!callbackProcessed) {
      setCallbackProcessed(true);
      handleOAuthCallback();
    }
  }, [isAuthenticated, loading, callbackProcessed]);

  const handleOAuthCallback = async () => {
    try {
      // Check URL params first, then sessionStorage (in case we redirected through login)
      let code = searchParams.get('code');
      let state = searchParams.get('state');
      
      if (!code) {
        code = sessionStorage.getItem('aps_callback_code');
        state = sessionStorage.getItem('aps_callback_state');
        // Clear stored params
        sessionStorage.removeItem('aps_callback_code');
        sessionStorage.removeItem('aps_callback_state');
      }
      
      const errorParam = searchParams.get('error');

      if (errorParam) {
        throw new Error(`OAuth error: ${errorParam}`);
      }

      if (!code) {
        throw new Error('No authorization code received');
      }

      setMessage('Exchanging authorization code for access token...');

      // Send the authorization code to our backend
      const response = await axios.post('/auth/aps/callback', {
        code: code,
        state: state
      });

      if (response.data.success) {
        setStatus('success');
        setMessage('Successfully authorized with Autodesk! You can now access your ACC/BIM 360 projects.');
        toast.success('APS authorization successful');
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Failed to complete authorization');
      }

    } catch (err) {
      console.error('APS OAuth callback error:', err);
      setStatus('error');
      setError(err.response?.data?.detail || err.message || 'Failed to complete authorization');
      setMessage('Authorization failed');
      toast.error('APS authorization failed');
    }
  };

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="card w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mb-4">
              <div className="w-16 h-16 mx-auto relative">
                <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin"></div>
              </div>
            </div>
            <CardTitle className="technical-header text-xl">
              VERIFYING SESSION
            </CardTitle>
            <CardDescription className="technical-subtext">
              Please wait while we verify your session...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="card w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-4">
            {status === 'processing' && (
              <div className="w-16 h-16 mx-auto relative">
                <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-orange-500" />
                </div>
              </div>
            )}
            {status === 'success' && (
              <CheckCircle className="w-16 h-16 mx-auto text-green-500" />
            )}
            {status === 'error' && (
              <AlertTriangle className="w-16 h-16 mx-auto text-red-500" />
            )}
          </div>
          
          <CardTitle className="technical-header text-xl">
            {status === 'processing' && 'PROCESSING AUTHORIZATION'}
            {status === 'success' && 'AUTHORIZATION SUCCESSFUL'}
            {status === 'error' && 'AUTHORIZATION FAILED'}
          </CardTitle>
          
          <CardDescription className="technical-subtext">
            {message}
          </CardDescription>
        </CardHeader>
        
        {status !== 'processing' && (
          <CardContent>
            {status === 'success' && (
              <div className="text-center">
                <div className="p-4 bg-green-50 border border-green-200 rounded-sm mb-4">
                  <p className="text-sm text-green-800">
                    🎉 You're all set! Redirecting to dashboard...
                  </p>
                </div>
                <p className="text-xs text-slate-600">
                  You can now browse your ACC/BIM 360 projects and generate public model URLs.
                </p>
              </div>
            )}
            
            {status === 'error' && (
              <div>
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
                <div className="text-center">
                  <button
                    onClick={() => navigate('/settings')}
                    className="btn-primary"
                    data-testid="go-to-settings"
                  >
                    GO TO SETTINGS
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}